import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { DrillableValue } from "@/components/owner-metrics/drillable-value";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ExecMonthlyRow,
  type ExecutiveMetricKey,
  formatLongMonthLabel,
  formatMetric,
  fyForMonth,
  METRIC_CONFIG,
  METRIC_ORDER,
  type YoyDeltas,
} from "./types";
import { YoyDelta } from "./yoy-delta";

interface MonthlyMetricsTableProps {
  rows: ExecMonthlyRow[];
  yoyByMonth: Record<string, YoyDeltas>;
  selectedFY: number | "all";
}

const DRILL_METRIC_MAP: Partial<
  Record<ExecutiveMetricKey, "opportunities" | "closedDeals" | "grossSales" | "netSales">
> = {
  opportunities: "opportunities",
  orders: "closedDeals",
  grossSales: "grossSales",
  netSales: "netSales",
};

export function MonthlyMetricsTable({ rows, yoyByMonth, selectedFY }: MonthlyMetricsTableProps) {
  const navigate = useNavigate();

  const display = useMemo(() => {
    const filtered =
      selectedFY === "all" ? rows : rows.filter((r) => fyForMonth(r.month) === selectedFY);
    return [...filtered].reverse();
  }, [rows, selectedFY]);

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 bg-background min-w-[160px]">
                Month
              </TableHead>
              {METRIC_ORDER.map((key) => (
                <TableHead key={key} className="text-right whitespace-nowrap">
                  {METRIC_CONFIG[key].label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {display.map((row) => {
              const yoy = yoyByMonth[row.month] ?? {};
              return (
                <TableRow key={row.month} className="hover:bg-muted/30">
                  <TableCell className="sticky left-0 z-10 bg-background font-medium whitespace-nowrap">
                    {formatLongMonthLabel(row.month)}
                  </TableCell>
                  {METRIC_ORDER.map((key) => {
                    const config = METRIC_CONFIG[key];
                    const drillKey = DRILL_METRIC_MAP[key];
                    const cellContent = (
                      <div className="flex flex-col items-end leading-tight">
                        <span className="tabular-nums">
                          {formatMetric(row[key], config.format)}
                        </span>
                        <YoyDelta delta={yoy[key]} size="xs" />
                      </div>
                    );
                    const onDrillClick = drillKey
                      ? () =>
                          navigate({
                            to: "/executive/drill/$metric/$month",
                            params: { metric: drillKey, month: row.month },
                          })
                      : undefined;
                    return (
                      <TableCell key={key} className="text-right">
                        <DrillableValue onClick={onDrillClick}>{cellContent}</DrillableValue>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
